import React, { useEffect, useState } from "react";
import toastr from "toastr";
import {
  Grid,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Box,
} from "@material-ui/core";
import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";

export interface AMC {
  company_name: string;
  amc_code: number;
  amc_inc_date: string;
  amc_inc_years: number;
  totalaum: number;
  isin_count: number;
  isin_list: string[];
  logo?: string | null;
  fund_document_link?: string | null;
}

const AMCLogoManager: React.FC = () => {
  const [amcs, setAmcs] = useState<AMC[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingFor, setSavingFor] = useState<number | null>(null);
  const [logoFiles, setLogoFiles] = useState<
    Record<number, File | null>
  >({});

  const loadAMCs = async () => {
    try {
      setLoading(true);

      const response: any =
        await makeGETRequestOnUserService(
          "/mutual_fund/all_funds_companies/"
        );

      setAmcs(response?.data?.data || []);
    } catch (error) {
      toastr.error("Failed to load AMCs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAMCs();
  }, []);

  const validateLogoFile = (file: File | null) => {
    if (!file) {
      return "Please select a file.";
    }

    const allowedExtensions = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/svg+xml",
      "image/webp",
    ];

    if (!allowedExtensions.includes(file.type)) {
      return "Only PNG, JPG, JPEG, SVG and WEBP files are allowed.";
    }

    const maxSize = 2 * 1024 * 1024;

    if (file.size > maxSize) {
      return "File size must be less than 2 MB.";
    }

    return null;
  };

  const updateLogo = async (amc: AMC) => {
    const file = logoFiles[amc.amc_code];

    const validationError = validateLogoFile(file);

    if (validationError) {
      toastr.error(validationError);
      return;
    }

    try {
      setSavingFor(amc.amc_code);

      const formData = new FormData();

      formData.append(
        "isin_list",
        JSON.stringify(amc.isin_list)
      );

      formData.append("logo", file as File);

      await makePOSTRequestOnUserService(
        "/mutual_fund/update_logo_and_fund_document_link_by_isin/",
        formData
      );

      toastr.success(
        `Logo updated for ${amc.company_name}`
      );

      setLogoFiles((prev) => ({
        ...prev,
        [amc.amc_code]: null,
      }));

      loadAMCs();
    } catch (error) {
      toastr.error("Failed to update logo.");
    } finally {
      setSavingFor(null);
    }
  };

  if (loading) {
    return (
      <Grid
        container
        justify="center"
        style={{ padding: 50 }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {amcs.map((amc) => {
        const isSaving = savingFor === amc.amc_code;
        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            lg={2}
            key={amc.amc_code}
          >
            <Card
              elevation={2}
              style={{
                borderRadius: 12,
                height: "100%",
              }}
            >
              <CardContent
                style={{
                  padding: 16,
                  textAlign: "center",
                }}
              >
                {/* Logo */}
                <Box
                  display="flex"
                  justifyContent="center"
                  mb={1.5}
                >
                  {logoFiles[amc.amc_code] ? (
                    <Avatar
                      src={URL.createObjectURL(
                        logoFiles[amc.amc_code] as File
                      )}
                      style={{
                        width: 60,
                        height: 60,
                      }}
                      variant="rounded"
                    />
                  ) : amc.logo?.trim() ? (
                    <Avatar
                      src={amc.logo}
                      style={{
                        width: 60,
                        height: 60,
                      }}
                      variant="rounded"
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      style={{
                        width: 60,
                        height: 60,
                        fontSize: 24,
                      }}
                    >
                      {amc.company_name?.charAt(0)}
                    </Avatar>
                  )}
                </Box>

                {/* Company Name */}
                <Typography
                  variant="subtitle2"
                  style={{
                    fontWeight: 600,
                    minHeight: 40,
                  }}
                >
                  {amc.company_name}
                </Typography>

                {/* Fund Count */}
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  style={{ marginBottom: 12 }}
                >
                  Total Funds: {amc.isin_list?.length || 0}
                </Typography>

                {/* File Upload */}
                <input
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  style={{ display: "none" }}
                  id={`logo-upload-${amc.amc_code}`}
                  type="file"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0] || null;

                    const validationError =
                      validateLogoFile(file);

                    if (validationError) {
                      toastr.error(validationError);
                      e.target.value = "";
                      return;
                    }

                    setLogoFiles((prev) => ({
                      ...prev,
                      [amc.amc_code]: file,
                    }));
                  }}
                />

                <label
                  htmlFor={`logo-upload-${amc.amc_code}`}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: 8 }}
                  >
                    Choose
                  </Button>
                </label>

                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={() => updateLogo(amc)}
                >
                  {isSaving
                    ? "Updating..."
                    : "Update"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default AMCLogoManager;